import { memo, useCallback, useMemo, useRef, useState } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { Button, IconButton } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import type { ISupportedLanguagesAndLocale } from "app/components/campaigns/campaigns-wizard/CampaignsWizardConsts";
import { getSupportedLanguagesForCountry } from "app/components/campaigns/searchEngine/inputFields/languageAndLocaleUtils/getSupportedLanguagesForCountry";
import type { ICampaignsLanguage } from "app/components/campaigns/searchEngine/SearchEnginesConsts";
import type { LocationDevice } from "app/pages/digital-marketing/keywords-campaigns/shared/types";
import { useGscDateRange } from "app/pages/digital-marketing/keywords-campaigns/shared/gsc-import/hooks/useGscDateRange";
import CountryService from "app/services/CountryService";
import { allTrackers } from "app/services/track/track";

import { useGscCountriesQuery, useGscKeywordsQuery } from "../../hooks";
import type { FixedGscMarketConfig, SavedGscMarket } from "../../types";
import { DeleteMarketModal } from "../DeleteMarketModal";

import { DevicePicker } from "./DevicePicker";
import { GoogleMarketActions } from "./GoogleMarketActions";
import { GoogleMarketSaved } from "./GoogleMarketSaved";
import { useKeywordTicks } from "./hooks";
import { KeywordMetrics } from "./KeywordMetrics";
import { getKeywordMetrics } from "./KeywordMetrics/utils";
import { KeywordsSlider } from "./KeywordsSlider";
import { LanguagePicker } from "./LanguagePicker";
import { LocationPicker } from "./LocationPicker";
import { MarketDropdown } from "./MarketDropdown";
import { NoDataAlert } from "./NoDataAlert";
import { MarketContainer } from "./styles";
import { getDisabledDevices, getFirstAvailableDevice, getHiddenCountryCodes } from "./utils";

const DEFAULT_DEVICE: LocationDevice = "BOTH";

type Props = {
    connectionId: number;
    site: string;
    languagesList: ISupportedLanguagesAndLocale[];
    marketConfigs?: FixedGscMarketConfig[];
    defaultConfigIndex?: number;
    disabledConfigIndices?: number[];
    isDropdownLocked?: boolean;
    savedMarket?: SavedGscMarket;
    // Markets already saved by the user (excluding this one). Used in the GSC-only flow
    // to derive disabled devices and hidden countries so the same country+device pair can't
    // be saved twice. Undefined in the fixedConfig flow (conflicts handled by the config itself).
    otherSavedMarkets?: SavedGscMarket[];
    onEditingChange?: (isEditing: boolean) => void;
    onSave: (market: SavedGscMarket, configIndex: number) => void;
    onDelete?: () => void;
};

/**
 * Configures a single market (country + language + device) for keyword import from a GSC property.
 *
 * Fetches available countries and keywords for the given site, then lets the user
 * pick country, language, device, and keyword count. Sensible defaults are auto-derived
 * from the fetched data; explicit user picks override them.
 *
 * Supports a save/edit cycle: the user can save the current configuration, which
 * collapses the form into a compact summary. Clicking "Edit" restores the full form.
 *
 * When `marketConfigs` is provided (pre-configured markets from the standard wizard),
 * a unified dropdown replaces the separate pickers, letting the user select among
 * the pre-defined market configurations.
 */
export const GoogleMarket = memo(
    ({
        connectionId,
        site,
        languagesList,
        marketConfigs,
        defaultConfigIndex,
        disabledConfigIndices,
        isDropdownLocked,
        savedMarket,
        otherSavedMarkets,
        onEditingChange,
        onSave,
        onDelete,
    }: Props) => {
        const { translate } = useTranslate();

        const defaultConfig = savedMarket ?? marketConfigs?.at(defaultConfigIndex ?? 0);
        const hasFixedMarkets = !!marketConfigs?.length;

        const [selectedCountry, setSelectedCountry] = useState<string | undefined>(defaultConfig?.countryCode);
        const [selectedLanguage, setSelectedLanguage] = useState<ICampaignsLanguage | undefined>(
            defaultConfig?.language,
        );
        const [selectedDevice, setSelectedDevice] = useState<LocationDevice>(defaultConfig?.device ?? DEFAULT_DEVICE);
        // Keyword count is tagged with the site it was chosen for so it
        // auto-invalidates when the user switches website properties.
        const [keywordCountSelection, setKeywordCountSelection] = useState<{ site: string; count: number } | undefined>(
            savedMarket ? { site, count: savedMarket.keywords.length } : undefined,
        );
        const selectedKeywordCount = keywordCountSelection?.site === site ? keywordCountSelection.count : undefined;

        const [selectedMarketIndex, setSelectedMarketIndex] = useState(defaultConfigIndex ?? 0);
        const [isEditing, setIsEditing] = useState(!savedMarket);
        const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

        const { startDate, endDate } = useGscDateRange();

        const countriesQuery = useGscCountriesQuery({
            connectionId,
            site,
            startDate,
            endDate,
        });

        // We hide duplicate locations based on saved markets
        const hiddenCountryCodes = useMemo(
            () => (otherSavedMarkets ? getHiddenCountryCodes(otherSavedMarkets) : []),
            [otherSavedMarkets],
        );

        const isEmptyCountries = !countriesQuery.isLoading && countriesQuery.data?.length === 0;

        const hasNoAvailableLocations = useMemo(() => {
            const countries = countriesQuery.data;
            if (!hiddenCountryCodes.length || !countries?.length) {
                return false;
            }
            return countries.every((c) => hiddenCountryCodes.includes(c.country));
        }, [countriesQuery.data, hiddenCountryCodes]);

        // Auto-select the first (highest-clicks) country until the user picks one.
        // Hidden countries (all devices taken) are skipped so we don't default into a duplicate.
        // When marketConfigs is provided, selectedCountry is already initialized from the first config.
        const country = useMemo(() => {
            if (selectedCountry) {
                return selectedCountry;
            }
            const firstAvailable = countriesQuery.data?.find((c) => !hiddenCountryCodes.includes(c.country));
            return firstAvailable?.country;
        }, [selectedCountry, countriesQuery.data, hiddenCountryCodes]);

        // We disable duplicate devices based on saved markets
        const disabledDevices = useMemo(
            () => (country && otherSavedMarkets ? getDisabledDevices(country, otherSavedMarkets) : []),
            [country, otherSavedMarkets],
        );

        // Effective device: falls back to the first available device when the user's pick
        // (or the initial default "BOTH") is disabled for the current country.
        const device = useMemo(() => {
            if (!disabledDevices.includes(selectedDevice)) {
                return selectedDevice;
            }
            return getFirstAvailableDevice(disabledDevices) ?? selectedDevice;
        }, [selectedDevice, disabledDevices]);

        // Derive default language from country: code -> name (via CountryService) -> first supported language.
        // The indirection through country name is because getSupportedLanguagesForCountry expects a name, not a code.
        const defaultLanguage = useMemo(() => {
            if (!country) {
                return undefined;
            }
            const countryName = CountryService.getCountryByCode(country)?.text;
            return countryName ? getSupportedLanguagesForCountry(countryName, languagesList)[0] : undefined;
        }, [country, languagesList]);

        // User's explicit pick, or the auto-derived default for the current country
        const language = selectedLanguage ?? defaultLanguage;

        // Keywords query depends on country + device; runs only when country is set (see hook's `enabled` flag)
        const keywordsQuery = useGscKeywordsQuery({
            connectionId,
            site,
            countryCode: country,
            device,
            startDate,
            endDate,
        });

        const handleMarketSelect = (index: number) => {
            const config = marketConfigs?.[index];
            if (!config) {
                return;
            }
            setSelectedMarketIndex(index);
            setSelectedCountry(config.countryCode);
            setSelectedLanguage(config.language);
            setSelectedDevice(config.device);
            setKeywordCountSelection(undefined);
        };

        const handleCountrySelect = (countryCode: string) => {
            if (!hasFixedMarkets) {
                allTrackers.dispatchTrackEvent("Drop Down", "click", "GSC Wizard/Market selected", {
                    availableCountries: countriesQuery.data?.length ?? 0,
                });
            }
            setSelectedCountry(countryCode);

            const countryName = CountryService.getCountryByCode(countryCode)?.text;
            // Reset language (depends on country) and keyword count (data changes per country)
            setSelectedLanguage(
                countryName ? getSupportedLanguagesForCountry(countryName, languagesList)[0] : undefined,
            );
            setKeywordCountSelection(undefined);

            // Auto-select the first available device for the new country
            if (otherSavedMarkets) {
                const disabled = getDisabledDevices(countryCode, otherSavedMarkets);
                const available = getFirstAvailableDevice(disabled);
                if (available) {
                    setSelectedDevice(available);
                }
            }
        };

        const handleDeviceSelect = (d: LocationDevice) => {
            setSelectedDevice(d);
            // Reset keyword count because keyword data is re-fetched per device
            setKeywordCountSelection(undefined);
        };

        const hasTrackedSlider = useRef(false);

        const handleSliderChange = useCallback(
            (value: number) => {
                if (!hasTrackedSlider.current && !hasFixedMarkets) {
                    hasTrackedSlider.current = true;
                    allTrackers.dispatchTrackEvent("Slider", "click", "GSC Wizard/Keyword slider used");
                }
                setKeywordCountSelection({ site, count: value });
            },
            [site, hasFixedMarkets],
        );

        const keywords = useMemo(() => keywordsQuery.data ?? [], [keywordsQuery.data]);
        const { ticks, defaultValue: defaultKeywordCount } = useKeywordTicks(keywords);
        const keywordCount = selectedKeywordCount ?? defaultKeywordCount;

        const isNoKeywordsData = keywordsQuery.data && keywordsQuery.data.length === 0;

        const handleSave = () => {
            if (!country || !language) {
                return;
            }

            if (!hasFixedMarkets) {
                const metrics = getKeywordMetrics(keywords, keywordCount);
                allTrackers.dispatchTrackEvent("Button", "click", "GSC Wizard/Website market saved", {
                    availableKeywords: keywords.length,
                    trackedKeywords: metrics.trackedKeywords,
                    impressionsP: metrics.impressionsRatio,
                    clicksP: metrics.clicksRatio,
                });
            }

            onSave(
                {
                    countryCode: country,
                    language,
                    device,
                    keywords: keywords.slice(0, keywordCount),
                },
                selectedMarketIndex,
            );
            handleEdit(false);
        };

        const handleEdit = (editing: boolean) => {
            setIsEditing(editing);
            onEditingChange?.(editing);
        };

        const handleCancel = () => {
            if (savedMarket) {
                setSelectedCountry(savedMarket.countryCode);
                setSelectedLanguage(savedMarket.language);
                setSelectedDevice(savedMarket.device);
                setKeywordCountSelection({ site, count: savedMarket.keywords.length });
            }
            handleEdit(false);
        };

        const handleDeleteClick = () => {
            setIsDeleteModalOpen(true);
        };
        const handleDeleteConfirm = () => {
            setIsDeleteModalOpen(false);
            onDelete?.();
        };
        const handleDeleteCancel = () => setIsDeleteModalOpen(false);

        if (!savedMarket && hasNoAvailableLocations) {
            return (
                <DeleteMarketModal
                    isOpen
                    hideTitle
                    description={translate("rankTracker.gsc.hasNoAvailableLocations")}
                    onConfirm={() => onDelete?.()}
                    confirmButtonType="primary"
                    confirmButtonText={translate("global.confirm")}
                />
            );
        }

        if (!savedMarket && isEmptyCountries) {
            return <NoDataAlert />;
        }

        if (!isEditing && savedMarket) {
            return (
                <>
                    <GoogleMarketSaved
                        savedMarket={savedMarket}
                        // We pass all keywords to show metrics based on selected keywords vs. real data
                        keywords={keywords}
                        onEdit={handleEdit}
                        onDelete={onDelete ? handleDeleteClick : undefined}
                    />
                    <DeleteMarketModal
                        isOpen={isDeleteModalOpen}
                        onCancel={handleDeleteCancel}
                        onConfirm={handleDeleteConfirm}
                    />
                </>
            );
        }

        const hasChanges =
            !savedMarket ||
            country !== savedMarket.countryCode ||
            language?.id !== savedMarket.language.id ||
            device !== savedMarket.device ||
            keywordCount !== savedMarket.keywords.length;

        const canSave = !!country && !!language && !keywordsQuery.isFetching && !isNoKeywordsData && hasChanges;

        return (
            <MarketContainer data-automation="gsc-market-edit">
                <Flex
                    alignItems="flex-start"
                    gap="8px"
                >
                    <Flex
                        flex="1"
                        flexDirection="column"
                        gap="20px"
                    >
                        {hasFixedMarkets ? (
                            <MarketDropdown
                                marketConfigs={marketConfigs}
                                selectedIndex={selectedMarketIndex}
                                onSelect={handleMarketSelect}
                                disabledIndices={disabledConfigIndices}
                                isDisabled={isDropdownLocked}
                            />
                        ) : (
                            <Flex
                                gap="16px"
                                alignItems="flex-start"
                            >
                                <LocationPicker
                                    isLoading={countriesQuery.isLoading}
                                    countries={countriesQuery.data ?? []}
                                    hiddenCountryCodes={hiddenCountryCodes}
                                    selectedCountryCode={country}
                                    onSelect={handleCountrySelect}
                                />
                                <LanguagePicker
                                    isDisabled={!country}
                                    languagesList={languagesList}
                                    selectedCountryCode={country}
                                    selectedLanguage={language}
                                    onSelect={setSelectedLanguage}
                                />
                                <DevicePicker
                                    isDisabled={!country}
                                    disabledDevices={disabledDevices}
                                    selectedDevice={device}
                                    onSelect={handleDeviceSelect}
                                />
                            </Flex>
                        )}
                        <>
                            <KeywordsSlider
                                isDisabled={!country || keywordsQuery.isFetching}
                                isFetching={keywordsQuery.isFetching}
                                totalKeywords={keywords.length}
                                ticks={ticks}
                                value={keywordCount}
                                onChange={handleSliderChange}
                            />
                            <KeywordMetrics
                                keywords={keywords}
                                selectedCount={keywordCount}
                                isLoading={keywordsQuery.isFetching}
                                selectedDevice={device}
                            />

                            {isNoKeywordsData ? <NoDataAlert /> : null}

                            <Flex
                                justifyContent="flex-end"
                                alignItems="center"
                                gap="4px"
                            >
                                {savedMarket ? (
                                    <Button
                                        type="flat"
                                        onClick={handleCancel}
                                        dataAutomation="gsc-market-cancel"
                                    >
                                        {translate("global.cancel")}
                                    </Button>
                                ) : null}
                                <IconButton
                                    type="outlined"
                                    iconName="checked"
                                    placement="left"
                                    iconSize="xs"
                                    isDisabled={!canSave}
                                    onClick={handleSave}
                                    dataAutomation="gsc-market-save"
                                >
                                    {translate("global.save")}
                                </IconButton>
                            </Flex>
                        </>
                    </Flex>
                    <GoogleMarketActions
                        onEdit={savedMarket ? handleEdit : undefined}
                        onDelete={onDelete ? handleDeleteClick : undefined}
                    />
                </Flex>

                <DeleteMarketModal
                    isOpen={isDeleteModalOpen}
                    onCancel={handleDeleteCancel}
                    onConfirm={handleDeleteConfirm}
                />
            </MarketContainer>
        );
    },
);
